import type React from 'react'
import { createContext, useContext, useEffect, useState } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { api } from './api'
import { clearDraftStorage } from './draft-storage'
import type { OrgMembership, User } from './types'

type AuthContextValue = {
  user: User | null
  org: OrgMembership | null
  loading: boolean
  signIn: (email: string, password: string) => Promise<void>
  signOut: () => Promise<void>
  /** Owners only: change how the org's clients show units. Stored values never change. */
  setUnitSystem: (unitSystem: OrgMembership['unitSystem']) => Promise<void>
}

const AuthContext = createContext<AuthContextValue | null>(null)

export function initialsFor(name: string) {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  const initials = parts.slice(0, 2).map((part) => part[0]?.toUpperCase() ?? '')
  return initials.join('') || name.slice(0, 2).toUpperCase()
}

type MeResponse = { user: User; orgs: OrgMembership[] }

export function AuthProvider({ children }: React.PropsWithChildren) {
  const [user, setUser] = useState<User | null>(null)
  const [org, setOrg] = useState<OrgMembership | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    api
      .get<MeResponse>('/me')
      .then(({ user, orgs }) => {
        // Drafts another person left on this device (their session expired without signing out).
        clearDraftStorage(user.id)
        setUser(user)
        setOrg(orgs[0] ?? null)
      })
      .catch(() => {
        setUser(null)
        setOrg(null)
      })
      .finally(() => setLoading(false))
  }, [])

  async function signIn(email: string, password: string) {
    const { user, orgs } = await api.post<MeResponse>('/auth/login', { email, password })
    clearDraftStorage(user.id)
    setUser(user)
    setOrg(orgs[0] ?? null)
  }

  async function signOut() {
    try {
      await api.post('/auth/logout')
    } catch {
      // The session may already be gone server-side; clear local state regardless.
    }
    // Unsent log drafts hold farm records: never leave them for the next person on this device.
    clearDraftStorage()
    setUser(null)
    setOrg(null)
  }

  async function setUnitSystem(unitSystem: OrgMembership['unitSystem']) {
    if (!org) return
    const updated = await api.patch<{ unitSystem: OrgMembership['unitSystem'] }>(`/orgs/${org.id}`, { unitSystem })
    setOrg({ ...org, unitSystem: updated.unitSystem })
  }

  return <AuthContext.Provider value={{ user, org, loading, signIn, signOut, setUnitSystem }}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider')
  return ctx
}

export function RequireAuth({ children }: React.PropsWithChildren) {
  const { user, loading } = useAuth()
  const location = useLocation()

  if (loading) return null
  if (!user) return <Navigate to="/login" state={{ from: location }} replace />

  return <>{children}</>
}
